import React from 'react';
import { 
  FileText, Image as ImageIcon, Type, Code2, Palette, 
  ShieldCheck, Share2, Calculator, Folder, Grid3x3, ArrowRight,
  Ruler, Zap, Clock, Database, ArrowLeftRight
} from 'lucide-react';
import { CategoryItem, ThemeMode } from '../types';
import { useI18n } from '../i18n/I18nContext';

interface CategoryGridProps {
  categories: CategoryItem[];
  theme: ThemeMode;
  onSelectCategory: (category: CategoryItem) => void;
}

export const CategoryGrid: React.FC<CategoryGridProps> = ({
  categories,
  theme,
  onSelectCategory
}) => {
  const isDark = theme === 'dark';
  const { t } = useI18n();

  const renderIcon = (iconName: string) => {
    const props = { className: "w-6 h-6 text-white" };
    switch (iconName) {
      case 'FileText': return <FileText {...props} />;
      case 'Image': return <ImageIcon {...props} />;
      case 'Type': return <Type {...props} />;
      case 'Code2': return <Code2 {...props} />;
      case 'Palette': return <Palette {...props} />;
      case 'ShieldCheck': return <ShieldCheck {...props} />;
      case 'Share2': return <Share2 {...props} />;
      case 'Calculator': return <Calculator {...props} />;
      case 'Folder': return <Folder {...props} />;
      case 'Ruler': return <Ruler {...props} />;
      case 'Zap': return <Zap {...props} />;
      case 'Clock': return <Clock {...props} />;
      case 'Database': return <Database {...props} />;
      case 'ArrowLeftRight': return <ArrowLeftRight {...props} />;
      case 'Grid':
      case 'Grid3x3': return <Grid3x3 {...props} />;
      default: return <FileText {...props} />;
    }
  };

  return (
    <section className="pt-6 pb-6 sm:pt-4 sm:pb-8 px-3 sm:px-6 lg:px-8 max-w-7xl mx-auto z-10 relative">
      
      {/* Section Header */}
      <div className="text-center mb-5 sm:mb-10">
        <h2 className={`text-lg sm:text-3xl font-bold tracking-tight ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}>
          {t('allCategoriesTitle', 'Explore Our Tool Categories')}
        </h2>
        <p className={`mt-1.5 sm:mt-2 text-xs sm:text-base px-2 ${
          isDark ? 'text-slate-400' : 'text-slate-600'
        }`}>
          {t('chooseCategorySub', 'Choose a category to explore our collection of free tools')}
        </p>
      </div>

      {/* Grid: 5 Columns on Large Desktop, 3 on Tablet/Medium, 2 on Small/Mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4 md:gap-5 px-1 sm:px-0">
        {categories.map((cat) => {
          return (
            <a
              key={cat.id}
              href={`/${cat.id}`}
              onClick={(e) => {
                if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                  e.preventDefault();
                  onSelectCategory(cat);
                }
              }}
              className={`group relative p-3 sm:p-5 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between ${
                isDark
                  ? `bg-[#0f121e]/90 border-slate-800/90 text-white ${cat.borderColorDark} hover:bg-[#131728] hover:-translate-y-1 shadow-lg shadow-black/40`
                  : `bg-white border-slate-200/90 text-slate-900 ${cat.borderColorLight} hover:border-purple-400 hover:shadow-xl hover:shadow-purple-500/10 hover:-translate-y-1 shadow-xs`
              }`}
            >
              <div>
                {/* Category Icon Badge with Gradient & Glow */}
                <div className="flex justify-center mb-2.5 sm:mb-4">
                  <div className={`relative w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-gradient-to-tr ${cat.gradientFrom} ${cat.gradientTo} flex items-center justify-center shadow-md ${cat.bgGlowClass} group-hover:scale-110 transition-transform duration-300`}>
                    <div className="scale-85 sm:scale-100">{renderIcon(cat.iconName)}</div>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-center text-[13.5px] sm:text-lg font-bold tracking-tight leading-tight group-hover:text-purple-600 transition-colors">
                  {t(cat.id + '_title', cat.title)}
                </h3>

                {/* Tool Count Pill */}
                <div className="text-center my-1.5 sm:my-2">
                  <span className={`inline-block text-[10px] sm:text-[11px] font-bold px-2 py-0.5 sm:px-2.5 rounded-full border ${
                    isDark ? 'bg-slate-900/80 border-slate-800 text-slate-300' : 'bg-slate-100 border-slate-200/80 text-purple-700 font-extrabold'
                  }`}>
                    {cat.countText}
                  </span>
                </div>

                {/* Description */}
                <p className={`text-center text-[10px] sm:text-xs leading-snug sm:leading-relaxed font-normal ${
                  isDark ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  {t(cat.id + '_desc', cat.description)}
                </p>
              </div>

              {/* Arrow Indicator at bottom */}
              <div className="mt-3 sm:mt-5 flex justify-center">
                <div className={`p-1 sm:p-1.5 rounded-full transition-all duration-300 ${
                  isDark
                    ? `${cat.iconColorDark} group-hover:translate-x-1 group-hover:bg-slate-800/60`
                    : `${cat.iconColorLight} group-hover:translate-x-1 group-hover:bg-purple-100/80`
                }`}>
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
              </div>
            </a>
          );
        })}
      </div>

    </section>
  );
};
